import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAppSettings, resolveImageUrl, useClientBrand } from "../context/AppSettingsContext";
import { PublicShell } from "./PublicHome";
import {
  User, Phone, Cake, Mail, CheckCircle2, MapPin, Home, Hash, Lock, Store, X,
  ChevronLeft, Sparkles, Gift, ArrowRight, MessageCircle, FileDown, Wallet,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import OtpVerify from "../components/OtpVerify";
import { PhoneField, EmailField, AddressCheck } from "../components/ContactFields";
import { emailCheck, phoneValid } from "../lib/contact";
import { getVisitSource } from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Sotto-componenti fuori dal componente principale: usano le variabili CSS del brand cliente
const brand = "var(--client-brand, #1E40AF)";
const labelColor = "var(--client-brand-dark, #1E3A8A)";
// Stessa palette bianco/blu dell'area cliente (ClientLogin)
const inputCls = "h-12 bg-white border-2 border-zinc-300 text-base";
// Campi obbligatori: fondino celeste 5% finché vuoti
const reqCls = (v) => `h-12 border-2 border-zinc-300 text-base ${v ? "bg-white" : "bg-sky-500/5"}`;

function formatErr(err) {
  const d = err?.response?.data?.detail;
  if (!d) return "Errore imprevisto";
  return typeof d === "string" ? d : JSON.stringify(d);
}

export default function PublicRegister() {
  const { brand, brandDark: labelColor } = useClientBrand();
  const { settings } = useAppSettings();

  const nav = useNavigate();
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState({
    first_name: "", last_name: "", phone: "", email: "", dob: "",
    password: "", password_confirm: "",
    address: "", city: "", zip_code: "", province: "",
    branch_id: new URLSearchParams(window.location.search).get("sede") || "",
    consent: false,
    consent_profiling: null,
    consent_marketing_email: null,
    consent_marketing_wa: null,
    referral_code: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [otpId, setOtpId] = useState(null);

  React.useEffect(() => {
    // Prefill referral from ?ref=CODE in URL
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get("ref");
      if (ref) setForm((f) => ({ ...f, referral_code: ref.toUpperCase() }));
    } catch {}
    axios.get(`${API}/public/branches`)
      .then((r) => setBranches(r.data || []))
      .catch(() => setBranches([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.consent !== true) return toast.error("Devi accettare il trattamento dati (consenso 1)");
    if (!form.first_name.trim() || !form.last_name.trim()) return toast.error("Inserisci nome e cognome");
    if (!phoneValid(form.phone)) return toast.error("Numero di telefono non valido");
    if (!emailCheck(form.email).valid) return toast.error("Email non valida: correggila prima di procedere");
    if (form.consent_profiling === null) return toast.error("Devi rispondere SI o NO al consenso Profilazione");
    if (form.consent_marketing_email === null) return toast.error("Devi rispondere SI o NO al consenso Marketing diretto");
    if (form.consent_marketing_wa === null) return toast.error("Devi rispondere SI o NO al consenso WhatsApp / SMS");
    if (!form.password || form.password.length < 6) return toast.error("Password minima 6 caratteri");
    if (form.password !== form.password_confirm) return toast.error("Le password non coincidono");
    if (!otpId) return toast.error("Verifica prima il tuo numero con il codice OTP");
    setLoading(true);
    try {
      // Strip the confirm field before sending — backend doesn't need it
      const { password_confirm: _pc, ...payload } = form;
      payload.name = `${form.first_name} ${form.last_name}`.trim();
      if (!payload.branch_id) delete payload.branch_id;
      payload.otp_id = otpId;
      const visitSrc = getVisitSource();
      if (visitSrc) payload.source = visitSrc;
      const { data } = await axios.post(`${API}/public/register`, payload);
      setSuccess(data);
      toast.success("Carta creata! 🎉");
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setLoading(false); }
  };

  return (
    <PublicShell title={success ? "Registrazione completata" : "Registrati"} back="/cliente" testid="public-register">
      <div className="light-auth -mx-4">
      <main className="max-w-md mx-auto px-6 py-2 space-y-6">
        {success ? (
          <SuccessCard success={success} nav={nav} />
        ) : (
          <>
            <div className="text-center">
              <div className="mx-auto h-14 w-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: `${brand}18`, color: brand }}>
                <Sparkles className="h-7 w-7" />
              </div>
              <span className="inline-block text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full mb-3" style={{ background: `${brand}12`, color: brand }}>
                Carta fedeltà online
              </span>
              <h1 className="font-heading font-extrabold text-2xl" style={{ color: brand }}>Registrati in 30 secondi.</h1>
              <p className="text-sm mt-1 font-medium" style={{ color: labelColor }} data-testid="register-subtitle">
                Ottieni subito la tua carta digitale, accumula punti anche sugli ordini online e sblocca premi.
              </p>
            </div>

            <form onSubmit={submit} className="space-y-6" data-testid="register-form">
              <Section icon={User} title="I tuoi dati">
                <div className="grid grid-cols-2 gap-3">
                  <Field icon={User} label="Nome*">
                    <Input data-testid="register-first-name" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={reqCls(form.first_name)} placeholder="Mario" />
                  </Field>
                  <Field icon={User} label="Cognome*">
                    <Input data-testid="register-last-name" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className={reqCls(form.last_name)} placeholder="Rossi" />
                  </Field>
                </div>
                <Field icon={Phone} label="Telefono* (prefisso + numero)">
                  <PhoneField value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required testPrefix="register-phone" className={`${reqCls(form.phone)} h-12`} selectClassName="h-12 bg-white border-2 border-zinc-300 text-zinc-800" />
                </Field>
                <Field icon={Mail} label="Email*">
                  <EmailField value={form.email} onChange={(v) => setForm({ ...form, email: v })} required light testPrefix="register-email" className={reqCls(form.email)} />
                </Field>
                <Field icon={Cake} label="Data di nascita (per il regalo compleanno)">
                  <Input data-testid="register-dob" type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className={inputCls} />
                </Field>
              </Section>

              <Section icon={Lock} title="Password per l'area riservata">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field icon={Lock} label="Password* (min. 6 caratteri)">
                    <Input data-testid="register-password" type="password" required minLength={6}
                           value={form.password}
                           onChange={(e) => setForm({ ...form, password: e.target.value })}
                           className={reqCls(form.password)} placeholder="••••••••" autoComplete="new-password" />
                  </Field>
                  <Field icon={Lock} label="Ripeti password*">
                    <Input data-testid="register-password-confirm" type="password" required minLength={6}
                           value={form.password_confirm}
                           onChange={(e) => setForm({ ...form, password_confirm: e.target.value })}
                           className={reqCls(form.password_confirm)} placeholder="••••••••" autoComplete="new-password" />
                  </Field>
                </div>
                {form.password && form.password_confirm && form.password !== form.password_confirm && (
                  <div className="text-xs text-red-600" data-testid="register-password-mismatch">Le password non coincidono</div>
                )}
              </Section>

              <Section icon={Store} title="La tua sede preferita (facoltativa)">
                <div className="grid grid-cols-2 gap-2" data-testid="register-branches">
                  <BranchChip testId="register-branch-any" on={!form.branch_id} onClick={() => setForm({ ...form, branch_id: "" })}
                              title="Nessuna preferenza" sub="Comunicazioni da tutte le sedi" />
                  {branches.map((b) => (
                    <BranchChip key={b.id} testId={`register-branch-${b.id}`} on={form.branch_id === b.id} onClick={() => setForm({ ...form, branch_id: b.id })}
                                title={b.name} sub={b.address} />
                  ))}
                </div>
              </Section>

              <Section icon={MapPin} title="Indirizzo di consegna (per ordini online)">
                <Field icon={Home} label="Via e numero civico">
                  <Input data-testid="register-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} placeholder="Via Roma 12" />
                </Field>
                <div className="grid grid-cols-6 gap-3">
                  <div className="col-span-2">
                    <Field icon={Hash} label="CAP">
                      <Input data-testid="register-zip" value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} className={inputCls} placeholder="84121" inputMode="numeric" />
                    </Field>
                  </div>
                  <div className="col-span-3">
                    <Field icon={MapPin} label="Città">
                      <Input data-testid="register-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} placeholder="Salerno" />
                    </Field>
                  </div>
                  <div className="col-span-1">
                    <Field icon={MapPin} label="Prov.">
                      <Input data-testid="register-province" maxLength={2} value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value.toUpperCase() })} className={`${inputCls} uppercase px-2 text-center`} placeholder="SA" />
                    </Field>
                  </div>
                </div>
                <AddressCheck form={form} onApply={(s) => setForm({ ...form, ...s })} light testPrefix="register-addr" />
              </Section>

              {/* REFERRAL — codice invito facoltativo */}
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 space-y-2" data-testid="register-referral-box">
                <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold flex items-center gap-2">
                  <Gift className="h-3.5 w-3.5" /> Hai un codice invito? (facoltativo)
                </div>
                <Input
                  data-testid="register-referral"
                  value={form.referral_code}
                  onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
                  className={`${inputCls} border-emerald-300 font-mono uppercase`}
                  placeholder="ES. W254875"
                />
                <p className="text-xs text-emerald-800">
                  Sia tu che il tuo amico riceverete <b>+50 punti</b> in regalo.
                </p>
              </div>

              {/* CONSENSI PRIVACY GDPR — 4 livelli granulari + informativa */}
              <Section icon={Lock} title="Consensi privacy (GDPR)" action={
                <span className="flex items-center gap-3">
                  {settings?.privacy_url && (
                    <a
                      href={resolveImageUrl(settings.privacy_url)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="register-privacy-link"
                      className="text-xs font-semibold underline normal-case tracking-normal"
                      style={{ color: brand }}
                    >
                      {settings.privacy_label || "Leggi l'informativa privacy"}
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => setPrivacyOpen(true)}
                    data-testid="register-privacy-open"
                    className="text-xs font-semibold hover:underline normal-case tracking-normal"
                    style={{ color: brand }}
                  >
                    {settings?.privacy_url ? "Riepilogo consensi" : "Leggi l'informativa"}
                  </button>
                </span>
              }>
                <div className="space-y-2">
                  <ConsentRow
                    testId="register-consent"
                    required
                    title="1. Trattamento dati* (obbligatorio)"
                    desc="Autorizzo il trattamento dei miei dati personali per la gestione della carta fedeltà e l'accumulo dei punti (base giuridica: contratto — art. 6.1.b GDPR)."
                    value={form.consent}
                    onChange={(v) => setForm({ ...form, consent: v })}
                  />
                  <ConsentRow
                    testId="register-consent-profiling"
                    title="2. Profilazione"
                    desc="Analisi delle mie abitudini di acquisto per ricevere offerte personalizzate in base ai miei gusti."
                    value={form.consent_profiling}
                    onChange={(v) => setForm({ ...form, consent_profiling: v })}
                  />
                  <ConsentRow
                    testId="register-consent-email"
                    title="3. Marketing diretto"
                    desc="Ricevo promozioni, coupon e novità via email e SMS."
                    value={form.consent_marketing_email}
                    onChange={(v) => setForm({ ...form, consent_marketing_email: v })}
                  />
                  <ConsentRow
                    testId="register-consent-wa"
                    title="4. Comunicazioni WhatsApp / SMS"
                    desc="Ricevo messaggi promozionali e coupon su WhatsApp o via SMS."
                    value={form.consent_marketing_wa}
                    onChange={(v) => setForm({ ...form, consent_marketing_wa: v })}
                  />
                </div>
                <p className="text-xs text-zinc-500">
                  Rispondi SI o NO a tutti e 4 i punti. Il consenso 1 è obbligatorio; gli altri sono revocabili in qualsiasi momento dall'area riservata.
                </p>
              </Section>

              <OtpVerify phone={form.phone} email={form.email} name={`${form.first_name} ${form.last_name}`.trim()} verifiedId={otpId} onVerified={setOtpId} light testPrefix="register-otp" />

              <Button
                data-testid="register-submit"
                type="submit"
                disabled={
                  loading
                  || !otpId
                  || form.consent !== true
                  || form.consent_profiling === null
                  || form.consent_marketing_email === null
                  || form.consent_marketing_wa === null
                }
                className="w-full h-12 text-white font-semibold text-base hover:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: brand }}
              >
                {loading ? "Attendere…" : "Attiva la mia carta fedeltà"} <ArrowRight className="h-4 w-4 ml-1" />
              </Button>

              <div className="text-center text-sm text-zinc-500 space-y-1">
                <div>
                  Hai già una carta?{" "}
                  <Link to="/cliente/accedi" className="font-semibold hover:underline" style={{ color: brand }} data-testid="register-goto-login">Accedi</Link>
                </div>
                <div className="text-xs">
                  Sei dello staff?{" "}
                  <Link to="/login" className="font-semibold hover:underline" style={{ color: brand }} data-testid="register-goto-staff">Accesso operatore</Link>
                </div>
              </div>
            </form>
          </>
        )}
      </main>
      </div>
      <PrivacyDialog open={privacyOpen} onOpenChange={setPrivacyOpen} settings={settings} />
    </PublicShell>
  );
}

function WalletAfterRegister({ success }) {
  const [status, setStatus] = useState(null);
  const [sent, setSent] = useState(false);
  useEffect(() => { axios.get(`${API}/public/wallet/status`).then(({ data }) => setStatus(data)).catch(() => setStatus({ apple: false, google: false })); }, []);
  if (!status || (!status.apple && !status.google)) return null;
  const card = success.card_number;
  const ua = navigator.userAgent || "";
  const isApple = /iPhone|iPad|iPod/i.test(ua), isAndroid = /Android/i.test(ua);
  const label = isApple && status.apple ? "Aggiungi ad Apple Wallet" : isAndroid && status.google ? "Salva su Google Wallet" : "Salva la card nel Wallet del telefono";
  const sendLink = async () => {
    try {
      const phone = success.customer?.phone, email = success.customer?.email;
      await axios.post(`${API}/public/wallet/recover`, phone ? { phone } : { email });
      setSent(true); toast.success(phone ? "Link Wallet inviato su WhatsApp" : "Link Wallet inviato via email");
    } catch (e) { toast.error("Invio non riuscito, riprova tra poco"); }
  };
  return (
    <div className="p-4 rounded-2xl border border-zinc-200 bg-white text-left space-y-3" data-testid="register-wallet">
      <div className="flex items-center gap-2"><Wallet className="h-4 w-4" style={{ color: brand }} /><div className="font-heading font-bold text-base">La card sempre con te</div></div>
      <p className="text-xs text-zinc-500">Salvala nel Wallet: punti, coupon e offerte lampo si aggiornano da soli, anche senza aprire l'app.</p>
      <a href={`${API}/public/wallet/${card}/save`} className="w-full h-12 rounded-md flex items-center justify-center gap-2 text-white font-semibold hover:brightness-95" style={{ background: "#111" }} data-testid="register-wallet-save">
        <Wallet className="h-4 w-4" /> {label}
      </a>
      {(success.customer?.phone || success.customer?.email) && (
        <button type="button" onClick={sendLink} disabled={sent} className="w-full h-10 rounded-md text-sm font-semibold border border-zinc-300 text-zinc-700 hover:bg-zinc-50 disabled:opacity-60" data-testid="register-wallet-send">
          {sent ? "Link inviato ✓" : success.customer?.phone ? "Inviami il link su WhatsApp (per salvarla dopo)" : "Inviami il link via email (per salvarla dopo)"}
        </button>
      )}
    </div>
  );
}

function SuccessCard({ success, nav }) {
  const c = success.welcome_coupon;
  return (
    <div className="text-center space-y-5 animate-fade-up" data-testid="register-success">
      <div className="mx-auto h-16 w-16 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
        <CheckCircle2 className="h-9 w-9" />
      </div>
      <div>
        <h2 className="font-heading text-2xl font-extrabold">
          {success.already_registered ? "Bentornato/a!" : "Tessera attivata"}
        </h2>
        <p className="text-sm text-zinc-500 mt-1">La tua carta è pronta. Salvala per ordini online e mostrala al negozio.</p>
      </div>
      <WalletAfterRegister success={success} />
      {c && (
        <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-left" data-testid="register-welcome-coupon">
          <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold mb-1 flex items-center gap-1"><Gift className="h-3.5 w-3.5" /> Coupon benvenuto</div>
          <div className="font-heading font-bold text-lg text-zinc-900">{c.name}</div>
          <div className="font-mono text-sm text-amber-800 mt-1">
            Codice: <span className="font-bold">{c.code}</span> · Sconto: {c.discount_type === "percent" ? `${c.discount_value}%` : `€ ${c.discount_value}`}
            {c.valid_until && <> · valido fino {c.valid_until}</>}
          </div>
        </div>
      )}
      <div className="inline-flex flex-col items-center gap-3 p-6 rounded-2xl border border-zinc-200 bg-white shadow-sm">
        <img
          src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=6&bgcolor=ffffff&color=1E40AF&data=${encodeURIComponent(success.card_number)}`}
          alt="QR" className="rounded-lg"
        />
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Numero carta</div>
        <div className="font-mono text-2xl font-bold" style={{ color: brand }} data-testid="register-card-number">{success.card_number}</div>
      </div>
      <div className="space-y-3">
        <Button
          data-testid="register-open-portal"
          onClick={() => nav(`/carta/${success.card_number}${new URLSearchParams(window.location.search).get("next") === "cart" ? "?cart=1" : ""}`)}
          className="w-full h-12 text-white font-semibold text-base hover:brightness-95"
          style={{ background: brand }}
        >
          Vai al mio portale <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
        {success.wa_welcome_link && (
          <a href={success.wa_welcome_link} target="_blank" rel="noreferrer"
             className="w-full h-12 rounded-md flex items-center justify-center gap-2 border-2 border-[#25D366] text-[#128C7E] font-semibold hover:bg-emerald-50"
             data-testid="register-wa">
            <MessageCircle className="h-4 w-4" /> Salva la carta su WhatsApp
          </a>
        )}
        {success.privacy_pdf && (
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}${success.privacy_pdf}`}
            target="_blank"
            rel="noreferrer"
            className="w-full h-12 rounded-md flex items-center justify-center gap-2 border border-zinc-300 text-zinc-700 font-semibold hover:bg-zinc-50"
            data-testid="register-privacy-pdf"
          >
            <FileDown className="h-4 w-4" /> Scarica consenso PDF
          </a>
        )}
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, action, children }) {
  return (
    <section className="rounded-2xl border border-zinc-200 bg-white p-4 sm:p-5 space-y-4 shadow-sm">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-extrabold" style={{ color: labelColor }}>
        <Icon className="h-3.5 w-3.5" style={{ color: brand }} /> {title}
        {action && <span className="ml-auto">{action}</span>}
      </div>
      {children}
    </section>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-semibold flex items-center gap-1.5" style={{ color: labelColor }}>
        <Icon className="h-3.5 w-3.5" style={{ color: brand }} /> {label}
      </Label>
      {children}
    </div>
  );
}

function BranchChip({ testId, on, onClick, title, sub }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`p-3 rounded-xl border-2 text-left transition-colors ${on ? "bg-blue-50 text-blue-900" : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300"}`}
      style={on ? { borderColor: brand } : {}}
    >
      <div className="text-sm font-semibold truncate">{title}</div>
      {sub && <div className="text-[11px] text-zinc-500 mt-0.5 truncate">{sub}</div>}
    </button>
  );
}

/** Riga consenso con toggle SI/NO obbligatorio. `value` è booleano o `null` (non ancora scelto). */
function ConsentRow({ testId, title, desc, value, onChange, required = false }) {
  const answered = value !== null && value !== undefined;
  const isNo = value === false;
  const isYes = value === true;
  return (
    <div
      data-testid={`${testId}-row`}
      className={`p-3 rounded-xl border ${
        required && !isYes
          ? "bg-sky-500/5 border-sky-300"
          : !answered
            ? "bg-sky-500/5 border-sky-200"
            : "bg-zinc-50 border-zinc-200"
      }`}
    >
      <div className="text-sm text-zinc-800 mb-2">
        <b>{title}</b>
        <div className="text-xs text-zinc-500 mt-0.5">{desc}</div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          data-testid={`${testId}-yes`}
          onClick={() => onChange(true)}
          className={`h-10 rounded-lg text-sm font-bold transition-all ${
            isYes
              ? "bg-emerald-500 text-white shadow-sm"
              : "bg-white border border-zinc-300 text-zinc-600 hover:border-emerald-500 hover:text-emerald-700"
          }`}
        >
          ✓ SI
        </button>
        <button
          type="button"
          data-testid={`${testId}-no`}
          disabled={required}
          onClick={() => onChange(false)}
          className={`h-10 rounded-lg text-sm font-bold transition-all ${
            isNo
              ? "bg-red-500 text-white shadow-sm"
              : "bg-white border border-zinc-300 text-zinc-600 hover:border-red-400 hover:text-red-600"
          } ${required ? "opacity-30 cursor-not-allowed" : ""}`}
          title={required ? "Il consenso base è obbligatorio per emettere la carta" : ""}
        >
          ✗ NO
        </button>
      </div>
      {!answered && (
        <div className="text-xs text-red-600 mt-1.5" data-testid={`${testId}-required-hint`}>
          Seleziona SI o NO
        </div>
      )}
    </div>
  );
}

/** Informativa privacy — dialog modale mostrata in fase di registrazione */
function PrivacyDialog({ open, onOpenChange, settings }) {
  const name = settings?.app_name || "Vivi Fidelity Club";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white text-zinc-900 border-zinc-200 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="register-privacy-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-zinc-900">
            <Lock className="h-4 w-4" style={{ color: brand }} /> Informativa Privacy — {name}
          </DialogTitle>
          <DialogDescription className="sr-only">Informativa sul trattamento dei dati personali</DialogDescription>
        </DialogHeader>
        <div className="text-sm text-zinc-600 space-y-3 leading-relaxed" data-testid="register-privacy-text">
          <p><b className="text-zinc-900">Titolare del trattamento:</b> {name}. I dati sono conservati sui nostri server e trattati esclusivamente per le finalità di seguito indicate, ai sensi del Reg. UE 2016/679 (GDPR).</p>

          <div>
            <div className="font-semibold text-amber-700 mb-1">1. Trattamento dati (obbligatorio)</div>
            <p>Base giuridica: <b>contratto</b> (art. 6.1.b GDPR). Utilizziamo il tuo nome, telefono ed email per gestire la tua carta fedeltà, accumulare punti, farti riscattare premi e coupon, e mantenere lo storico dei tuoi acquisti. Senza questo consenso non è possibile emettere la carta.</p>
          </div>

          <div>
            <div className="font-semibold text-zinc-900 mb-1">2. Profilazione (facoltativo)</div>
            <p>Base giuridica: <b>consenso</b>. Analizziamo la frequenza dei tuoi acquisti, i prodotti preferiti e la tua sede abituale per proporti offerte più rilevanti. Puoi revocare questo consenso in qualsiasi momento e continueremo a farti accumulare punti normalmente.</p>
          </div>

          <div>
            <div className="font-semibold text-zinc-900 mb-1">3. Marketing diretto — email &amp; SMS (facoltativo)</div>
            <p>Base giuridica: <b>consenso</b>. Ti inviamo promozioni, coupon di benvenuto, campagne stagionali e novità del negozio via email o SMS. Ogni comunicazione contiene un link per la disiscrizione immediata.</p>
          </div>

          <div>
            <div className="font-semibold text-zinc-900 mb-1">4. Comunicazioni WhatsApp (facoltativo)</div>
            <p>Base giuridica: <b>consenso</b>. Ti scriviamo direttamente su WhatsApp per coupon personalizzati, promemoria di scadenza premi, conferma prenotazioni e messaggi promozionali. Puoi revocare rispondendo STOP a qualunque messaggio.</p>
          </div>

          <div className="border-t border-zinc-200 pt-3">
            <div className="font-semibold text-zinc-900 mb-1">Conservazione dei dati</div>
            <p>I dati sono conservati per la durata di attività della carta e fino a 24 mesi dall'ultima transazione, dopodiché sono anonimizzati. I consensi facoltativi possono essere revocati in qualunque momento dall'area riservata → Impostazioni → Privacy.</p>
          </div>

          <div>
            <div className="font-semibold text-zinc-900 mb-1">I tuoi diritti (art. 15-22 GDPR)</div>
            <p>Puoi in qualsiasi momento chiedere <b>accesso</b>, <b>rettifica</b>, <b>cancellazione</b>, <b>limitazione</b> o <b>portabilità</b> dei tuoi dati, oppure presentare reclamo al Garante Privacy. Basta scrivere al titolare del trattamento dal negozio o tramite il modulo di contatto sull'app.</p>
          </div>
        </div>
        <button
          onClick={() => onOpenChange(false)}
          data-testid="register-privacy-close"
          className="mt-3 w-full h-11 rounded-md text-white text-sm font-semibold flex items-center justify-center gap-2 hover:brightness-95"
          style={{ background: brand }}
        >
          <X className="h-4 w-4" /> Ho letto — Chiudi
        </button>
      </DialogContent>
    </Dialog>
  );
}
